import { Link } from "@remix-run/react";
import type { IMedia } from "~/types";
import { ListItem } from "~/shared";
import dotsBackground from "~/assets/images/inkusion/bg-dot-pattern-lightgray.png";
import waveWhite from "~/assets/images/inkusion/wave-bg-top-white.svg";
import curvbackground from "~/assets/images/start_how_itworsk_wave.svg";
import theme from "~/theme/default";
import specialArticleImg from "~/assets/images/special-article-home-img.png";
import { classNames, getImageUrl, toSafePathSegment } from "~/utils";
import { UnifiedCardHome } from "~/components";

/* ------------------------------------------------------------------ */
/*  Main export                                                        */
/* ------------------------------------------------------------------ */
export function FachArticleHome({
  articleData,
  specialArticlCount,
}: {
  articleData: IMedia[];
  specialArticlCount?: number;
}) {
  const { button } = theme;
  const { base } = button;

  const articleItems = articleData.slice(0, 6);

  const articleLink = (id: number, name: string, slug?: string) =>
    `/professional_article/main/${id}/${toSafePathSegment(slug || name)}`;

  return (
    <div
      className="relative pt-20 pb-12 md:py-36"
      style={{
        backgroundImage: `linear-gradient(to right, #153d71, #005283, #006792, #007c9e, #1f91a7)`,
      }}
    >
      {/* Dots overlay */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${dotsBackground})`,
          backgroundSize: "70%",
          backgroundPosition: "right center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Wave at top */}
      <div className="absolute top-0 z-40 hidden w-full md:block">
        <img alt="" className="w-full object-fill" src={curvbackground} />
      </div>

      <div className="section-wrapper relative z-10 mt-8 md:mt-14 2xl:mt-24">
        <section>
          <h2 className="text-2xl font-bold text-white lg:text-[40px]">
            Aktuelle Fachbeiträge
          </h2>
          <p className="my-3 max-w-3xl text-base leading-relaxed text-white">
            Von theologischer Vertiefung bis hin zu praxisnahen Ideen: Unsere
            Fachbeiträge unterstützen nicht nur bei der Unterrichtsvorbereitung,
            sondern bieten auch Orientierung zu aktuellen Themen.
          </p>

          <div className="mt-8 flex flex-col gap-8 lg:flex-row ">
            {/* Left column: 6 article cards + CTA button */}
            <div className="lg:col-span-3">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {articleItems.map((article) => (
                  <ListItem
                    key={article.id}
                    to={articleLink(article.id, article.name, article.slug)}
                    className="w-full"
                  >
                    <UnifiedCardHome
                      name={article.name}
                      subtitle={article.authorsAsString ?? ""}
                      imageUrl={
                        article.firstPicture
                          ? getImageUrl(article.firstPicture.file.url)
                          : ""
                      }
                      layout="horizontal"
                    />
                  </ListItem>
                ))}
              </div>

              {/* CTA button */}
              <div className="mt-10 flex w-full justify-center">
                <Link
                  to="/professional_article/main"
                  className={classNames(
                    base,
                    "ml-3 inline-flex items-center rounded-full border border-transparent bg-btnColor-secondary px-6 py-2 text-xs font-medium text-white shadow-sm hover:bg-btnColor-hoveSecondary focus:outline-none focus:ring-2 focus:ring-btnColor-secondary focus:ring-offset-2 md:px-8 md:text-sm"
                  )}
                >
                  Entdecke {specialArticlCount} Fachbeiträge
                </Link>
              </div>
            </div>

            {/* Right-side illustration */}
            <div className="hidden  lg:flex lg:items-center lg:justify-center">
              <img src={specialArticleImg} alt="" className="object-contain" />
            </div>
          </div>
        </section>
      </div>

      {/* Wave at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-40 hidden w-full md:block">
        <img
          alt="wave-white"
          className="w-full object-contain"
          src={waveWhite}
        />
      </div>
    </div>
  );
}
